<template>
  <Card
    tone="default"
    padding="md"
    class="group flex h-full flex-col gap-3 transition-colors duration-150 hover:border-accent hover:bg-accent focus-visible:shadow-[var(--shadow-ring-focus)] focus-visible:outline-none"
    as="article"
    :interactive="true"
    :aria-label="useAriaLabel"
    role="button"
    data-testid="promo-template-card"
    @click="emit('use')"
    @keydown="onKeydown"
  >
    <div class="flex items-start justify-between gap-2">
      <div
        class="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-accent-soft text-accent-strong transition-colors duration-150 group-hover:bg-paper group-hover:text-accent-strong"
      >
        <BaseIcon :name="icon" :size="18" />
      </div>
      <BaseTag
        tone="default"
        :aria-label="`${timelineLabel}: ${timeline}`"
        class="transition-colors duration-150 group-hover:bg-paper group-hover:text-accent-strong"
      >
        {{ timeline }}
      </BaseTag>
    </div>

    <h3
      class="text-base font-bold text-ink-strong transition-colors duration-150 group-hover:text-ink-inverse md:text-lg"
    >
      {{ title }}
    </h3>
    <p
      class="text-sm text-ink-muted transition-colors duration-150 group-hover:text-ink-inverse/90"
    >
      {{ description }}
    </p>

    <div class="flex flex-col gap-2">
      <p
        class="text-[0.6875rem] font-semibold tracking-[0.12em] text-ink-muted uppercase transition-colors duration-150 group-hover:text-ink-inverse/80"
      >
        {{ traitsLabel }}
      </p>
      <ul
        class="flex flex-col gap-1.5 text-sm text-ink transition-colors duration-150 group-hover:text-ink-inverse"
      >
        <li v-for="trait in traits" :key="trait" class="flex items-start gap-2">
          <span
            class="mt-1.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-accent-strong transition-colors duration-150 group-hover:bg-ink-inverse"
          />
          <span>{{ trait }}</span>
        </li>
      </ul>
    </div>

    <span
      class="mt-auto inline-flex items-center gap-1.5 pt-2 text-sm font-semibold text-accent-strong transition-colors duration-150 group-hover:text-ink-inverse group-hover:underline"
      data-testid="promo-template-cta"
    >
      <span>{{ useCtaLabel }}</span>
      <BaseIcon name="arrow-right" :size="14" />
    </span>
  </Card>
</template>

<script setup lang="ts">
/**
 * `PromoTemplateCard` — карточка готового шаблона промо.
 *
 * Визуальное семейство с `ModuleCard` (accent-chip иконка +
 * радиус + тон). В отличие от модуля карточка — focusable region
 * с `role=button`: Enter / Space эмитят `use`, что поднимается в
 * секцию и открывает LeadForm-модалку.
 */
import Card from '@/components/primitives/Card.vue';
import BaseTag from '@/components/base/BaseTag.vue';
import BaseIcon from '@/components/base/BaseIcon.vue';
import type { IconName } from '@/components/base/icons';

interface Props {
  icon: IconName;
  title: string;
  description: string;
  traits: readonly string[];
  timeline: string;
  traitsLabel: string;
  useCtaLabel: string;
  timelineLabel: string;
  /**
   * Composed a11y label for the focusable card (title + CTA). Not
   * prefixed with `aria`, чтобы избежать авто-forwarding на корневой
   * элемент через attrs — значение ставится явно через `:aria-label`.
   */
  useAriaLabel: string;
}

defineProps<Props>();

const emit = defineEmits<{
  (e: 'use'): void;
}>();

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    emit('use');
  }
}
</script>
